
import React from 'react';
import { Property, AppUser, UserRole } from '../../../types';
import { formatCurrency, formatCommissionRate } from '../../../utils/formatters';
import { MapPinIcon, BedIcon, BathIcon, RulerMeasureIcon, BuildingOfficeIcon, CarIcon } from '../../Icons';
import { ALLOWED_REFERRAL_PROPERTY_TYPES } from '../../../constants';
import { PropertyCommentBadge } from './PropertyCommentBadge';

interface PropertyCardInfoProps {
  property: Property;
  isCompact: boolean;
  appUser: AppUser | null;
  resolvedCommissionRate: number;
  displayCode: string;
  isUpdated: boolean;
  setCopyModalState: (state: { isOpen: boolean; text: string; title: string }) => void;
  commentCount?: number;
}

export const PropertyCardInfo: React.FC<PropertyCardInfoProps> = ({
  property, isCompact, appUser, resolvedCommissionRate, displayCode, isUpdated, setCopyModalState, commentCount = 0
}) => {
  const canSeeOwner = appUser && [
    UserRole.SUPERADMIN, 
    UserRole.ADMIN, 
    UserRole.OPERACION, 
    UserRole.COMERCIAL_INTERNO,
    UserRole.BROKER
  ].includes(appUser.role);

  const exhibitsCreditCommission = ALLOWED_REFERRAL_PROPERTY_TYPES.includes(property.tipoinmueble);
  const totalCommissionRate = resolvedCommissionRate + (exhibitsCreditCommission ? 0.004 : 0);

  return (
    <div className={`${isCompact ? 'p-3' : 'p-4'} flex-grow flex flex-col`}>
      <div className="flex justify-between items-start mb-1 gap-2">
        <p className="text-xl font-bold text-gray-900">{formatCurrency(property.precio_venta)}</p>
        {appUser && appUser.role !== UserRole.CLIENTE && (
          <div className={`${
            resolvedCommissionRate <= 0 
              ? 'bg-red-600' 
              : exhibitsCreditCommission 
                ? 'bg-gradient-to-r from-[#00234F] to-pink-600 border border-[#00234F]/10' 
                : 'bg-green-500'
          } text-white text-[10px] sm:text-[11px] font-black px-2.5 py-1 rounded shadow-sm text-center shrink-0`}>
            {exhibitsCreditCommission ? `${formatCommissionRate(totalCommissionRate)}*` : formatCommissionRate(resolvedCommissionRate)}
          </div>
        )}
      </div>

      {appUser && appUser.role !== UserRole.CLIENTE && exhibitsCreditCommission && (
        <div className="mt-1 mb-2 bg-pink-50/70 border border-pink-100 rounded-lg p-1.5 text-left transform hover:scale-[1.01] transition-all">
          <p className="text-[9px] font-extrabold tracking-tight text-pink-950 uppercase leading-normal">
            ✨ Comisión total: {formatCommissionRate(resolvedCommissionRate)} venta + 0.4% crédito Aliados
          </p>
        </div>
      )}

      <div className="flex items-center justify-between gap-1.5 mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[11px] uppercase tracking-wider font-extrabold text-gray-800 bg-gray-100 px-2 py-0.5 rounded shadow-sm border border-gray-200 truncate">
            {property.tipoinmueble}
          </span>
          {commentCount > 0 && <PropertyCommentBadge count={commentCount} />}
        </div>
        <div 
          onClick={(e) => { e.stopPropagation(); setCopyModalState({ isOpen: true, text: (isUpdated && property.codigo_actual) || displayCode, title: 'Copiar Código' }); }} 
          className="text-[11px] px-2 py-0.5 rounded-md bg-sky-50 hover:bg-sky-100 cursor-pointer transition-all flex items-center font-mono font-black text-sky-700 border border-sky-200/80 shadow-xs shrink-0"
        >
          {isUpdated && property.codigo_actual ? (
            <><span className="line-through text-gray-400 mr-1.5 text-[9px]">{property.codigo}</span><span className="text-sky-750 font-black">{property.codigo_actual}</span></>
          ) : <span className="tracking-tight">{displayCode}</span>}
        </div>
      </div>

      {/* Dirección añadida */}
      <p className="text-[11px] text-gray-700 font-bold uppercase truncate mb-1">
        {property.direccion}
      </p>

      <p className="text-[11px] text-gray-500 flex items-center mb-2">
        <MapPinIcon className="w-3.5 h-3.5 mr-1" />
        <span className="truncate uppercase font-medium">
          {[property.neighborhoodName, property.localidad, property.ciudad].filter(Boolean).join(', ')}
        </span>
      </p>

      <div className="flex items-center gap-3 text-gray-600 text-[10px] mb-3 font-bold flex-wrap">
        {property.area_construida_original && <div className="flex items-center"><RulerMeasureIcon className="w-3.5 h-3.5 mr-1" />{property.area_construida_original}</div>}
        {property.num_habitaciones > 0 && <div className="flex items-center"><BedIcon className="w-3.5 h-3.5 mr-1" />{property.num_habitaciones}</div>}
        {property.num_banos > 0 && <div className="flex items-center"><BathIcon className="w-3.5 h-3.5 mr-1" />{property.num_banos}</div>}
        {property.num_parqueaderos > 0 && <div className="flex items-center"><CarIcon className="w-3.5 h-3.5 mr-1" />{property.num_parqueaderos}</div>}
      </div>

      {canSeeOwner && property.owner && (
        <div className="flex items-center gap-2 bg-gray-50/80 border border-gray-100 px-3 py-1.5 rounded-lg mb-3">
          <BuildingOfficeIcon className="w-3.5 h-3.5 text-sky-600" />
          <span className="text-[10px] font-black text-gray-700 uppercase truncate">Dueño: {property.owner.replace(/_/g, ' ')}</span>
        </div>
      )}
    </div>
  );
};
